import { IconBranch } from "@/assets/icons/geist/IconBranch";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { Button } from "@/components/flexkit/Button";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Toggle } from "@/components/Toggle";
import { useBranch } from "@/hooks/use-branch";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteSession, useSessionName } from "@/hooks/use-sessions";
import { getSessionType, getSessionUrl } from "./session-model";
import { type Session } from "./session-model";
import { useEnvironment } from "@/queries/environment-queries";
import { getOnaUrl } from "@/routes/ai/ai-urls";
import { EnvironmentChanges } from "@/routes/environments/SidebarEnvironmentList";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type DeleteSessionModalProps = {
    session: Session;
    open: boolean;
    onClose: () => void;
};

export const DeleteSessionModal: FC<DeleteSessionModalProps> = ({
    session,
    open,
    onClose,
}: DeleteSessionModalProps) => {
    const { toast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const type = getSessionType(session);
    const name = useSessionName(session);
    const lastUsedEnvironmentId = session.environmentIds[session.environmentIds.length - 1];
    const [deleteEnvironment, setDeleteEnvironment] = useState(false);

    const deleteSession = useDeleteSession(session);

    useEffect(() => {
        setDeleteEnvironment(type == "standalone-environment");
    }, [type]);

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    const handleDeleteSession = useCallback(async () => {
        try {
            await deleteSession.deleteAsync({ deleteEnvironments: deleteEnvironment ? [lastUsedEnvironmentId] : [] });
            const sessionUrl = getSessionUrl(session);
            const onaUrl = getOnaUrl();
            toast({
                title: "Session deleted",
            });
            if (location.pathname === sessionUrl) {
                navigate(onaUrl);
            } else {
                onOpenChange(false);
            }
        } catch (error) {
            toast({
                title: "Failed to delete session",
                description: formatError(error),
            });
        }
    }, [
        deleteEnvironment,
        deleteSession,
        lastUsedEnvironmentId,
        location.pathname,
        navigate,
        onOpenChange,
        session,
        toast,
    ]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.DeleteSessionModal}
                data-testid="delete-session-modal"
            >
                <DialogHeader>
                    <DialogTitle>Delete session</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <Text>
                        Are you sure you want to delete the session{" "}
                        <span className="font-bold" translate="no">
                            {name}
                        </span>
                        ?
                    </Text>
                    {type != "standalone-agent-execution" && (
                        <InputField
                            label="Delete environment"
                            id="delete-environment"
                            hint="Deleting the environment will result in stopping the environment and removing any content on the environment."
                        >
                            <div className="flex flex-row items-center gap-2">
                                <Toggle
                                    state={deleteEnvironment ? "checked" : "unchecked"}
                                    onToggle={(checked) => Promise.resolve(setDeleteEnvironment(checked))}
                                />
                                <EnvironmentCard environmentId={lastUsedEnvironmentId} />
                            </div>
                        </InputField>
                    )}
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <DialogClose asChild>
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteSession.isPending}
                        onClick={handleDeleteSession}
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const EnvironmentCard: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const branch = useBranch(environment);

    if (!environment) {
        return null;
    }
    return (
        <div className="group flex w-fit flex-col justify-between gap-4 overflow-x-hidden rounded-xl border border-border-base bg-surface-glass px-4 py-2.5 pl-3">
            <div className="flex flex-row items-center gap-1">
                <EnvironmentStatusDot env={environment} size="sm" className="m-0 size-4 shrink-0" />
                <div className="flex min-w-0 items-center gap-0.5">
                    <IconBranch size="sm" className="size-3 shrink-0 text-content-primary" />
                    <span className="truncate text-sm text-content-primary" translate="no">
                        {branch}
                    </span>
                </div>
                <EnvironmentChanges git={environment?.status?.content?.git} className="text-[10px]" />
            </div>
        </div>
    );
};
